import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Leicestershire\'s RED DfT rating guarantee my claim will succeed?',
    answer: 'No. A RED rating is official evidence that the Department for Transport considers Leicestershire\'s highway maintenance performance weak overall — particularly on spend — but Section 58 still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Combined with the council\'s own admissions about reactive maintenance and rising pothole volumes, it can support your case; it does not replace photos, location data, repair invoices and proof of when the defect existed.',
  },
  {
    question: 'Is Leicestershire County Council the same authority as Leicester City Council?',
    answer: 'No. Leicestershire County Council maintains county roads across the shire — towns, villages and rural routes outside the city boundary. Leicester City Council is a separate unitary authority responsible for roads within the city of Leicester. If your pothole was inside the city, your claim goes to Leicester City Council, not the county. Motorways and some major A-roads are maintained by National Highways.',
  },
  {
    question: 'Why is the overall rating RED when condition is only AMBER?',
    answer: 'The DfT assigns separate scorecards. Leicestershire is RED overall because spend is RED and best practice is AMBER, while published network condition data earns AMBER — not green. That pattern means the council\'s investment and maintenance approach flagged concerns even where headline condition scores are middling. For a claim, aggregate ratings matter less than whether your specific road was inspected and repaired within the council\'s own timescales.',
  },
  {
    question: 'What does "this reactive approach is unsustainable" actually mean?',
    answer: 'In its 2025 transparency report, Leicestershire acknowledges that reduced funding has pushed it towards urgent repairs to keep roads safe "rather than improving their overall condition and restoring the structural integrity of the asset," and states plainly that "this reactive approach is unsustainable." That is the council describing a cycle of patching failures instead of preventing them — and admitting the approach cannot continue indefinitely without further deterioration.',
  },
  {
    question: 'Pothole repairs rose 47% from 2020/21 to 2024/25 — does that help my case?',
    answer: 'It can provide context. The council filled 5,770 potholes in 2020/21 and 8,467 in 2024/25 — a 46.7% rise on its own figures. Higher reactive volumes alongside falling preventative spend share (43.55% in 2021/22 to a projected 30.24% in 2025/26) suggest defects are forming faster than prevention stabilises the network. That does not prove your specific pothole was neglected, but it undermines any blanket argument that the system is working well.',
  },
  {
    question: 'Why has preventative spending dropped if the council cites funding pressure?',
    answer: 'The council links reduced planned maintenance to funding constraints, yet its 2025/26 transparency report shows a DfT capital allocation of £28.8m against actual capital spend of £25.7m — meaning not all allocated capital was used. The DfT\'s RED spend scorecard reflects how investment compares to need and allocation. For claimants, the gap between stated asset-management principles and declining preventative share is documented in the council\'s own tables.',
  },
  {
    question: 'How do I report a pothole to Leicestershire County Council?',
    answer: 'Use the council\'s online Report a road problem form at leicestershire.gov.uk. The council states reported potholes are inspected and classified within 14 days; category 1 hazards are targeted for repair within three working days, while category 2 defects have a 90-day target from public report. Prior reports of the same defect strengthen a claim by demonstrating notice. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function LeicestershireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Leicestershire Pothole Claims | 8,467 Repairs on a RED-Rated Network | Fixtyer"
        description="Leicestershire County Council is RED-rated by the DfT on overall and spend scorecards, filled 8,467 potholes in 2024/25, and admits its reactive approach is unsustainable. What that means for your Section 58 claim."
        canonical="/councils/leicestershire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Leicestershire', url: '/councils/leicestershire' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Leicestershire County Council"
        councilSlug="leicestershire"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/leicestershire"
        headline="Leicestershire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Leicestershire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            8,467 Pothole Repairs in 2024/25 on a RED-Rated County Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Leicestershire County Council — distinct from Leicester City Council — earns a{' '}
            <strong className="text-rose-400">RED</strong> overall DfT rating, with{' '}
            <strong className="text-rose-400">RED</strong> on spend and{' '}
            <strong className="text-amber-400">AMBER</strong> on condition and best practice. Its own transparency
            report records <strong className="text-rose-400">8,467 pothole repairs in 2024/25</strong>, up 47% from
            2020/21, while estimated preventative spend share falls to a projected{' '}
            <strong className="text-amber-400">30.24%</strong> in 2025/26. The council admits:{' '}
            <em>"this reactive approach is unsustainable."</em> Section 58 still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">+47%</div>
            <div className="text-white font-medium mt-2">More pothole repairs since 2020/21</div>
            <div className="text-brand-300 text-sm mt-1">
              From 5,770 repairs in 2020/21 to 8,467 in 2024/25 — with 8,866 projected for 2025/26 — while
              preventative maintenance share drops from 43.55% to a projected 30.24% over the same period.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Department for Transport ratings 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            One of 13 local highway authorities in England rated RED overall — condition is AMBER, not green
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full">
              <caption className="sr-only">Leicestershire County Council DfT road maintenance ratings 2025 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall rating</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition scorecard</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Spend scorecard</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Wider best practice scorecard</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <p className="text-amber-800 text-sm">
              <strong>Rating caveat:</strong> RED overall does not mean every road is failing. Condition is{' '}
              <strong>AMBER</strong> — middling on published survey data — while spend is{' '}
              <strong>RED</strong>, reflecting how the DfT assesses investment against allocation and network need.
              DfT ratings measure authority-level performance from transparency reports; they do not determine
              liability for an individual pothole. Section 58 turns on inspection records and repair response for
              your specific defect.
            </p>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
            <h3 className="font-semibold text-rose-900 mb-3">What RED overall means in context</h3>
            <p className="text-rose-800 mb-3 text-sm">
              For 2025/26, 13 of England's local highway authorities received an overall RED rating. Leicestershire
              is among them — driven primarily by the RED spend scorecard alongside AMBER condition and best practice.
            </p>
            <ul className="text-rose-800 space-y-2 text-sm">
              <li>• <strong>Spend RED:</strong> investment and spending patterns flagged as inadequate relative to DfT assessment</li>
              <li>• <strong>Condition AMBER:</strong> published network condition data is mixed — not the worst tier, but not green</li>
              <li>• <strong>Best practice AMBER:</strong> maintenance programme practices sit below the highest standard</li>
            </ul>
          </div>

          <p className="mt-4 text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            4,332km of county roads — mostly unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Leicestershire's 2025 transparency report — where most pothole claims happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">4,332km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
              <div className="text-xs text-surface-500 mt-1">2,690 miles</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">2,361km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">54% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,547km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">423km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">Some maintained by National Highways</div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">550 miles</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Amber/red — needs planned maintenance</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">199 miles</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Red — requires structural repairs</div>
            </div>
          </div>

          <p className="text-surface-600 text-sm mb-6">
            The council's condition summary categorises 550 miles as needing planned maintenance and 199 miles as
            requiring structural repairs to prevent further damage — roughly 20% of the network flagged for intervention
            on its own published figures. Roads within Leicester city and some trunk routes fall to other authorities.
          </p>

          <QuoteCard
            quote="Reduction in funding has had an impact on how we have been able to carry out planned maintenance and has resulted in more urgent repairs being needed to keep the asset in a serviceable condition (keeping the assets safe for users, rather than improving their overall condition and restoring the structural integrity of the asset). This reactive approach is unsustainable."
            source="Leicestershire County Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising pothole repair volumes
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — defects requiring a patch to make the road safe, per the council's own report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Leicestershire estimated potholes filled 2020 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">5,770</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">5,862</td>
                  <td className="px-6 py-4 text-center text-amber-600">+1.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">6,007</td>
                  <td className="px-6 py-4 text-center text-amber-600">+4.1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold">8,066</td>
                  <td className="px-6 py-4 text-center text-rose-600">+39.8%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8,467</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+46.7%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-700">8,866</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Nearly 23 repairs per day</h3>
              <p className="text-surface-600 text-sm">
                8,467 pothole repairs in 2024/25 works out to roughly 23 patches per day across 4,332km. A network
                producing that volume of safety-critical defects is one where individual potholes routinely form
                between planned preventative treatments — especially on U-roads, which make up 54% of carriageway length.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Repairs are not proof of prevention</h3>
              <p className="text-amber-800 text-sm">
                More pothole fills mean more reactive workload, not better overall condition. The council itself
                distinguishes keeping assets "safe for users" from "improving their overall condition" — and states
                the reactive path is unsustainable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Prevention vs reactive */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Prevention share falling while repairs rise
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Estimated share of highway maintenance spend — preventative versus reactive, from the council's transparency report
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Leicestershire preventative and reactive maintenance spend share</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Prevention per £1 reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center text-emerald-400">42.39%</td>
                  <td className="px-4 py-3 text-center">20.53%</td>
                  <td className="px-4 py-3 text-center">—</td>
                </tr>
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2021/22</td>
                  <td className="px-4 py-3 text-center text-emerald-400 font-bold">43.55%</td>
                  <td className="px-4 py-3 text-center">20.03%</td>
                  <td className="px-4 py-3 text-center text-emerald-400 font-bold">£2.17</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center text-amber-400">36.16%</td>
                  <td className="px-4 py-3 text-center">21.31%</td>
                  <td className="px-4 py-3 text-center">—</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center text-amber-400">37.85%</td>
                  <td className="px-4 py-3 text-center">22.34%</td>
                  <td className="px-4 py-3 text-center">—</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center text-rose-400">33.63%</td>
                  <td className="px-4 py-3 text-center">22.65%</td>
                  <td className="px-4 py-3 text-center">—</td>
                </tr>
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center text-rose-400 font-bold">30.24%</td>
                  <td className="px-4 py-3 text-center">21.50%</td>
                  <td className="px-4 py-3 text-center text-rose-400 font-bold">£1.41</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">Prevention investment declining</h3>
              <p className="text-brand-200 text-sm">
                Estimated preventative share fell from 43.55% in 2021/22 to a projected 30.24% in 2025/26 — a
                13-percentage-point drop. For every £1 spent on reactive repairs, the council projected spending
                £2.17 on prevention in 2021/22 but only £1.41 in 2025/26.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Planned work vs backlog</h3>
              <p className="text-brand-200 text-sm">
                For 2024/25 the council planned 141.3 miles of preventative treatment (resurfacing, patching and
                surface dressing) against 550 miles needing planned maintenance and 199 miles requiring structural
                repairs. 2025/26 plans include 3.6 miles of resurfacing and 45 miles of surface dressing — a
                fraction of the published backlog.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Budget */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Following the money — RED spend scorecard
          </h2>
          <p className="text-surface-600 mb-8">
            2025/26 budget figures from the council's transparency report
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">£28.8m</div>
              <div className="text-sm text-surface-700 font-medium mt-1">DfT capital allocation</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">£25.7m</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Actual capital spend</div>
              <div className="text-xs text-amber-700 mt-1">Below full allocation</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">£23.4m</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Revenue spend</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">£49.1m</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total budget</div>
            </div>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
            <h3 className="font-semibold text-rose-900 mb-3">Why spend is RED despite a £49m budget</h3>
            <p className="text-rose-800 text-sm mb-3">
              The DfT spend scorecard assesses investment against allocation and network need — not headline budget
              size alone. Leicestershire's report shows capital spend below its DfT allocation, a declining preventative
              share, and rising pothole repair volumes. Aggregate spend does not prove every defect was caught within
              inspection intervals.
            </p>
            <p className="text-rose-800 text-sm">
              The council cites funding pressure; its own tables show the gap between asset-management principles
              (extend operational life through preventative treatments) and a falling preventative spend share.
            </p>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Leicestershire acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On preventative maintenance benefits</h3>
              <QuoteCard
                quote="Planned preventative maintenance... generally results in a progressive improvement in overall condition for longer periods of time, less frequent interventions required, improved public satisfaction, a gradual reduction in third party claims, more cost-effective long-term treatments..."
                source="Leicestershire County Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On reactive maintenance and claims</h3>
              <QuoteCard
                quote="[The Council] will aim to minimise reactive type of repairs, particularly on its unclassified network, as it generally results in lower public satisfaction, an increase in third party injury claims, the need for more interventions and higher maintenance costs."
                source="Leicestershire County Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On asset management principles</h3>
              <QuoteCard
                quote="One of the supporting principles of our Highway Asset Management Policy is that we will aim to extend the operational life of highway assets using appropriately timed preventative and restorative treatments."
                source="Leicestershire County Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a RED-rated county council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Leicestershire's RED rating and council admissions strengthen context — but specificity wins
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Condition scorecard is AMBER — not the lowest tier on published survey data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £49.1m total highways budget with formal asset-management policy
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented inspection and classification regime for reported potholes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Reactive repairs demonstrate active defect response
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a prepared Section 58 defence citing inspection intervals and repair targets. Generic
                "council neglect" arguments alone will not suffice.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall and RED spend scorecards from the DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admission: "this reactive approach is unsustainable"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 8,467 pothole repairs in 2024/25 — up 47% since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Preventative spend share projected at 30.24% — lowest in six published years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 199 miles requiring structural repairs; 550 miles needing planned maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council links reactive maintenance to "an increase in third party injury claims"
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Leicestershire must
              maintain highways it is responsible for. To defend a claim under{' '}
              <strong className="text-white">Section 58</strong>, it must show a reasonable system for inspecting
              and repairing the specific defect — not just publish a DfT scorecard.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm mb-6">
              <li>• Was your pothole reported before your incident — creating a record the council had notice?</li>
              <li>• Did the defect meet the council's intervention criteria during routine safety inspections?</li>
              <li>• Do photos show defect age beyond the council's classification and repair targets?</li>
              <li>• Was the road on the U-road network (54% of carriageway) where the council aims to minimise reactive repairs?</li>
            </ul>
            <p className="text-white font-medium">
              A RED rating and the council's own admissions provide authority-level context. Your claim still
              lives or dies on the specific defect, prior reports, and dated photographic evidence.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Leicestershire County Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us
            when you reported it so we can reference it in your pack. Problems inside Leicester city go to
            Leicester City Council; motorways and some major A-roads go to National Highways.
          </p>
          <a
            href="https://www.leicestershire.gov.uk/roads-and-travel/road-maintenance/report-a-road-problem"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a road problem — leicestershire.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-xs mt-4">
            Council stated targets: inspection and classification within 14 days of public report; category 1
            hazards repaired within three working days; category 2 defects within 90 calendar days.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Leicestershire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED-rated county demands a precise, evidence-led claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No RED rating or spend scorecard cited</li>
                <li>• No council "unsustainable" admission quoted</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ DfT RED overall and spend scorecards documented</li>
                <li>✅ Council's "unsustainable" admission included</li>
                <li>✅ 8,467 pothole repairs in 2024/25 cited</li>
                <li>✅ Preventative spend decline evidenced</li>
                <li>✅ Section 58 rebuttal tailored to Leicestershire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=leicestershire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Leicestershire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.leicestershire.gov.uk/roads-and-travel/road-maintenance/local-highways-maintenance-and-improvements"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Leicestershire County Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Pothole reporting targets from{' '}
            <a
              href="https://www.leicestershire.gov.uk/roads-and-travel/road-maintenance/report-a-road-problem"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Leicestershire County Council — Report a road problem
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
